import React from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { COMPANY_INFO } from "../data/siteData";

export default function TermsPage() {
  return (
    <>
      <Helmet>
        <title>Handelsbetingelser | NF Polering</title>
        <meta
          name="description"
          content="Se NF Polerings handelsbetingelser for vinduespudsning på Vestsjælland. Information om priser, betaling, aflysning, reklamation og abonnementsaftaler."
        />
        <meta property="og:title" content="Handelsbetingelser | NF Polering" />
        <meta
          property="og:description"
          content="Se NF Polerings handelsbetingelser for vinduespudsning på Vestsjælland. Information om priser, betaling, aflysning, reklamation og abonnementsaftaler."
        />
        <link rel="canonical" href="https://nf-polering.dk/handelsbetingelser" />
      </Helmet>

      <div className="bg-slate-50 text-slate-900 py-14 border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="inline-flex items-center gap-2 text-cyan-600 text-xs font-bold uppercase tracking-wider mb-2">
            <i className="fa-solid fa-file-contract" aria-hidden="true" />
            <span>Juridiske Vilkår</span>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900">
            Handelsbetingelser – NF Polering
          </h1>
          <p className="mt-3 text-slate-600 text-sm">
            <strong>Senest opdateret: 19/11/2025</strong>
          </p>
        </div>
      </div>

      <div className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 prose prose-slate max-w-none text-slate-700 space-y-8 leading-relaxed">
          <p className="text-base font-medium text-slate-800">
            Disse handelsbetingelser gælder for alle aftaler mellem <strong>NF Polering</strong> og virksomhedens kunder, både private og erhverv.
          </p>

          <div className="border-t border-slate-200 pt-6">
            <h2 className="text-xl font-bold text-slate-900 mb-3">1. Virksomhedsoplysninger</h2>
            <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 text-sm space-y-1">
              <p><strong>NF Polering</strong></p>
              <p>Slagelse 4200</p>
              <p>CVR: 35 01 70 89</p>
              <p>Mail: <a href="mailto:kontakt@nf-polering.dk" className="text-cyan-600 font-medium">kontakt@nf-polering.dk</a></p>
              <p>Tlf.: <a href="tel:30146070" className="text-cyan-600 font-medium">+45 30 14 60 70</a></p>
            </div>
          </div>

          <div className="border-t border-slate-200 pt-6">
            <h2 className="text-xl font-bold text-slate-900 mb-3">2. Ydelser</h2>
            <p>NF Polering tilbyder:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Vinduespolering for <strong>private og erhverv</strong></li>
              <li>Både enkeltopgaver og <strong>abonnementsordninger</strong></li>
            </ul>
            <p className="mt-2">Andre ydelser kan udføres efter særskilt aftale.</p>
          </div>

          <div className="border-t border-slate-200 pt-6">
            <h2 className="text-xl font-bold text-slate-900 mb-3">3. Booking af opgaver</h2>
            <p>Kunden kan bestille via:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Kontaktformular på hjemmesiden</li>
              <li>Direkte via mail</li>
              <li>Direkte via telefon</li>
            </ul>
            <p className="mt-2">For abonnementsordninger aftales frekvens individuelt (f.eks. hver 4. eller 8. uge).</p>
          </div>

          <div className="border-t border-slate-200 pt-6">
            <h2 className="text-xl font-bold text-slate-900 mb-3">4. Priser</h2>
            <p>Priser oplyses i tilbuddet og fastsættes ud fra bl.a.:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Antal vinduer</li>
              <li>Frekvens</li>
              <li>Adresse og adgangsforhold</li>
              <li>Evt. særlige ønsker</li>
            </ul>
            <p className="mt-2">Hvis opgavens omfang viser sig at være væsentligt anderledes end oplyst, kan prisen justeres efter aftale.</p>
          </div>

          <div className="border-t border-slate-200 pt-6">
            <h2 className="text-xl font-bold text-slate-900 mb-3">5. Betaling</h2>
            <div className="space-y-4">
              <div>
                <h3 className="font-bold text-slate-800">Private kunder</h3>
                <p>Betaling sker som udgangspunkt via <strong>MobilePay eller bankoverførsel efter udført pudsning</strong>.</p>
                <p>Ved udvendig polering, hvor kunden ikke er hjemme, fremsendes faktura pr. mail med <strong>8 dages betalingsfrist</strong>.</p>
              </div>
              <div>
                <h3 className="font-bold text-slate-800">Erhvervskunder</h3>
                <p>Betaling sker via <strong>faktura/bankoverførsel</strong>.</p>
                <p>Standard betalingsfrist er <strong>8 dage fra fakturadato</strong>, medmindre andet er aftalt skriftligt.</p>
              </div>
              <div>
                <h3 className="font-bold text-slate-800">For sen betaling</h3>
                <p>Ved for sen betaling kan der pålægges <strong>rykkergebyr og morarente</strong> i henhold til renteloven.</p>
              </div>
            </div>
          </div>

          <div className="border-t border-slate-200 pt-6">
            <h2 className="text-xl font-bold text-slate-900 mb-3">6. Afbestilling og forgæves kørsel</h2>
            <div className="space-y-4">
              <div>
                <h3 className="font-bold text-slate-800">Afbestilling</h3>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Afbestilling eller flytning af en aftale skal ske <strong>senest 24 timer før</strong> det aftalte tidspunkt.</li>
                  <li>Ved aflysning <strong>mindre end 24 timer før</strong> forbeholder NF Polering sig retten til at fakturere <strong>50 % af den aftalte pris</strong>.</li>
                  <li>Ved udeblivelse uden aflysning kan <strong>hele det aftalte beløb</strong> faktureres.</li>
                </ul>
              </div>
              <div>
                <h3 className="font-bold text-slate-800">Forgæves kørsel / manglende adgang</h3>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Hvis arbejdet ikke kan udføres pga. manglende adgang (låst port/dør, løse hunde, blokerede vinduer m.m.), betragtes det som forgæves kørsel.</li>
                  <li>I sådanne tilfælde kan der opkræves <strong>op til 50 % af den aftalte pris</strong> for at dække tid og kørsel.</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="border-t border-slate-200 pt-6">
            <h2 className="text-xl font-bold text-slate-900 mb-3">7. Adgangsforhold</h2>
            <p>Kunden er ansvarlig for, at:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Vinduerne er tilgængelige</li>
              <li>Vindueskarme og vinduesområder er ryddet i det omfang, det er nødvendigt</li>
              <li>Dyr er forsvarligt sikret</li>
              <li>Haver, møbler, buske m.m. ikke blokerer for adgangen til vinduerne</li>
            </ul>
            <p className="mt-2">
              NF Polering flytter som udgangspunkt ikke tunge møbler, krukker, havemøbler m.m. af hensyn til tid, sikkerhed og forsikring. Hvis vinduer ikke kan tilgås, pudses de ikke, og der kan faktureres for forgæves fremmøde.
            </p>
          </div>

          <div className="border-t border-slate-200 pt-6">
            <h2 className="text-xl font-bold text-slate-900 mb-3">8. Vejrforhold</h2>
            <ul className="list-disc pl-5 space-y-1">
              <li>Ved kraftig regn, sne, storm, glatte forhold eller meget lave temperaturer kan NF Polering <strong>udskyde vinduespoleringen</strong> af hensyn til sikkerhed og kvalitet.</li>
              <li>Kunden betaler ikke ekstra for ombooking pga. vejret.</li>
              <li>Snavs og striber, der opstår som følge af vejrforhold <strong>efter</strong> udført pudsning, er ikke omfattet af reklamationsretten.</li>
            </ul>
          </div>

          <div className="border-t border-slate-200 pt-6">
            <h2 className="text-xl font-bold text-slate-900 mb-3">9. Reklamation og tilfredshed</h2>
            <ul className="list-disc pl-5 space-y-1">
              <li>Hvis kunden ikke er tilfreds med den udførte polering, skal reklamation ske <strong>hurtigst muligt og senest inden for 24–48 timer</strong> efter udført arbejde.</li>
              <li>Reklamation skal ske på mail eller telefon og meget gerne med <strong>billeder</strong> af de pågældende ruder.</li>
              <li>Er reklamationen berettiget, kommer NF Polering og <strong>udbedrer fejlen uden beregning</strong>.</li>
              <li>Reklamationen omfatter ikke snavs/striber, der opstår pga. vejr eller forhold, der er opstået efter NF Polerings besøg (f.eks. regn, støv, havearbejde osv.).</li>
            </ul>
          </div>

          <div className="border-t border-slate-200 pt-6">
            <h2 className="text-xl font-bold text-slate-900 mb-3">10. Abonnementsordninger</h2>
            <ul className="list-disc pl-5 space-y-1">
              <li>Abonnementsaftaler er <strong>løbende</strong>, indtil de opsiges.</li>
              <li>Der er <strong>ingen lang binding</strong>, men aftalen kan opsiges med <strong>løbende måned + 1 måned</strong> med mindre andet er aftalt.</li>
              <li>Ændring af frekvens (f.eks. fra hver 4. uge til hver 8. uge) kan ske efter aftale.</li>
              <li>Ved gentagne pauser, ændringer eller aflysninger kan prisen justeres eller aftalen opsiges af NF Polering.</li>
            </ul>
          </div>

          <div className="border-t border-slate-200 pt-6">
            <h2 className="text-xl font-bold text-slate-900 mb-3">11. Kørsel og geografisk område</h2>
            <p>NF Polering arbejder primært i <strong>Vestsjælland</strong>.</p>
            <p className="mt-1">Større opgaver uden for området kan udføres efter særskilt aftale og evt. kørselstillæg.</p>
          </div>

          <div className="border-t border-slate-200 pt-6">
            <h2 className="text-xl font-bold text-slate-900 mb-3">12. Ansvar og begrænsninger</h2>
            <p>NF Polering er ansvarlig efter dansk rets almindelige regler, men:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Der tages forbehold for eksisterende skader på ruder, rammer og lister.</li>
              <li>NF Polering kan ikke gøres ansvarlig for ridser, der skyldes tidligere forhold, slid eller fejl på glas/rammer.</li>
              <li>Fastgroet skidt som maling, mørtel, cement m.m. fjernes ikke ved almindelig vinduespolering og kræver særskilt aftale.</li>
            </ul>
          </div>

          <div className="border-t border-slate-200 pt-6">
            <h2 className="text-xl font-bold text-slate-900 mb-3">13. Ændringer af handelsbetingelser</h2>
            <p>NF Polering kan løbende opdatere disse handelsbetingelser. Seneste version vil altid være tilgængelig på hjemmesiden.</p>
          </div>

          <div className="pt-8 border-t border-slate-200">
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-cyan-600 text-white font-bold text-sm hover:bg-cyan-700 transition-colors"
            >
              <i className="fa-solid fa-arrow-left" aria-hidden="true" />
              <span>Tilbage til forsiden</span>
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}
